import { z } from "zod";
import { PROJECT_FORMATS } from "./types.js";

export const identifierSchema = z
  .string()
  .trim()
  .min(5, "Введите корпоративную почту или рабочий номер")
  .refine(
    (value) =>
      /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value) || /^[+\d][\d\s()\-]{8,}$/.test(value),
    "Это не похоже на почту или телефон",
  );

export const loginSchema = z.object({
  identifier: identifierSchema,
  password: z.string().min(1, "Введите пароль"),
});

export const createProjectSchema = z.object({
  title: z.string().trim().min(2, "Название должно быть длиннее").max(120),
  format: z.enum(PROJECT_FORMATS),
  logline: z.string().trim().max(400).optional().default(""),
  genre: z.string().trim().max(80).optional().default(""),
  language: z.enum(["ru", "en"]).optional().default("ru"),
});

export const updateProjectSchema = z.object({
  title: z.string().trim().min(2).max(120).optional(),
  logline: z.string().trim().max(400).optional(),
  synopsis: z.string().trim().max(6000).optional(),
  genre: z.string().trim().max(80).optional(),
  status: z
    .enum(["development", "preproduction", "production", "postproduction", "delivered", "archived"])
    .optional(),
  tags: z.array(z.string().trim()).max(20).optional(),
  cover: z
    .object({
      color: z.string(),
      accent: z.string(),
      pattern: z.enum(["signal", "grid", "arc", "noise"]),
      assetId: z.string().optional(),
    })
    .optional(),
});

export const createDocumentSchema = z.object({
  title: z.string().trim().min(1).max(160),
  kind: z.enum(PROJECT_FORMATS).optional(),
  episodeNumber: z.number().int().min(0).max(999).optional(),
});

export const createCharacterSchema = z.object({
  name: z.string().trim().min(1).max(120),
  role: z
    .enum(["lead", "supporting", "guest", "antagonist", "mentor", "background"])
    .optional()
    .default("supporting"),
  oneLiner: z.string().trim().max(300).optional().default(""),
  bio: z.string().trim().max(6000).optional().default(""),
  goal: z.string().trim().max(2000).optional().default(""),
  conflict: z.string().trim().max(2000).optional().default(""),
  arc: z.string().trim().max(3000).optional().default(""),
  color: z.string().optional(),
});

export const commentSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  blockId: z.string().nullable().optional(),
  parentId: z.string().nullable().optional(),
  rangeStart: z.number().int().min(0).optional(),
  rangeEnd: z.number().int().min(0).optional(),
  quote: z.string().max(600).optional(),
  mentions: z.array(z.string()).optional().default([]),
});

export const aiRunSchema = z.object({
  agentIds: z.array(z.string()).min(1).max(8),
  brief: z.string().trim().min(3).max(4000),
  focus: z.string().trim().max(120).optional().default("scene"),
  documentId: z.string().optional(),
  blockId: z.string().optional(),
});

export type CreateProjectInput = z.infer<typeof createProjectSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateCharacterInput = z.infer<typeof createCharacterSchema>;
